import { useFeatureAvailable, useSoftDeployAccess } from '../hooks';
import { useTranslation } from 'Components/Helpers/translate';
import { Feature } from 'Store/actions/FeatureHelperActions';
import SignalsBar from './components/SignalsBar/SignalsBar';
import BemClass from '@upsales/components/Utils/bemClass';
import AISignalsResource from 'App/resources/AISignals';
import AISignals from 'App/resources/Model/AISignals';
import Contact from 'App/resources/Model/Contact';
import { Flex, Text } from '@upsales/components';
import Client from 'App/resources/Model/Client';
import { openDrawer } from 'Services/Drawer';
import React, { useEffect } from 'react';

import './RelationsSignalWidget.scss';

type RelationsSignalWidgetProps = {
	clientId: number;
	client?: Client;
	contactId?: number;
	contact?: Contact;
	barOnly?: boolean;
	signals?: AISignals;
};

const SignalsWidget = ({ clientId, contactId, contact, barOnly = false, signals }: RelationsSignalWidgetProps) => {
	const classes = new BemClass('RelationsSignalWidget');
	const [signalScore, setSignalScore] = React.useState<AISignals | null>(signals ?? null);
	const { t } = useTranslation();

	useEffect(() => {
		const fetchSignalScore = async () => {
			if (signals) {
				return;
			}
			const reqContactId = contactId || contact?.id;
			const { data } = await AISignalsResource.getEntityTotalSignals(
				reqContactId ? 'contact' : 'client',
				reqContactId || clientId
			);
			setSignalScore(data);
		};

		fetchSignalScore();
	}, [clientId, contactId, signals]);

	const hasSignals = signalScore && (signalScore.positive || signalScore.negative);

	return !hasSignals ? null : (
		<Flex
			className={classes.mod({ barOnly }).b()}
			gap="u4"
			justifyContent="space-between"
			alignItems="center"
			onClick={e => {
				e.stopPropagation();
				openDrawer('RelationsSignalDrawer', { clientId, contactId: contactId || contact?.id });
			}}
		>
			{!barOnly ? (
				<Text className={classes.elem('relationText').b()} bold>
					{contactId ? t('default.contactRelation') : t('default.clientRelation')}
				</Text>
			) : null}
			<div className={classes.elem('signals').b()}>
				<SignalsBar signals={signalScore} periodText={`(${t('default.lastNMonths', { months: 6 })})`} />
			</div>
		</Flex>
	);
};

const RelationsSignalWidget = ({ clientId, contactId, contact, barOnly, signals }: RelationsSignalWidgetProps) => {
	const hasAISignalsWidget = useSoftDeployAccess('AI_SIGNALS_WIDGET');
	const hasAI = useFeatureAvailable(Feature.AI);
	return !(hasAISignalsWidget && hasAI) ? null : (
		<SignalsWidget
			clientId={clientId}
			contactId={contactId}
			contact={contact}
			barOnly={barOnly}
			signals={signals}
		/>
	);
};

export default RelationsSignalWidget;
